
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin } from 'lucide-react';

interface ListingCardProps {
  listing: {
    id: string;
    title: string;
    image: string;
    price?: string;
    location: string;
    breeder: string;
    breed: string;
    listing_type?: string;
    evaluationData?: {
      calvingEase?: string;
      birthWeight?: string;
      weaningWeight?: string;
      milk?: string;
    };
  };
}

const ListingCard: React.FC<ListingCardProps> = ({ listing }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  return (
    <Link to={`/listing/${listing.id}`}>
      <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
        <div className="aspect-[3/2] overflow-hidden rounded-t-lg relative">
          {listing.listing_type && (
            <span className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded z-10">
              {listing.listing_type}
            </span>
          )}
          {imageLoading && (
            <Skeleton className="w-full h-full absolute inset-0" />
          )}
          <img 
            src={listing.image} 
            alt={listing.title}
            className={`w-full h-full object-cover hover:scale-105 transition-all duration-300 ${
              imageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
          {imageError && !imageLoading && (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center transition-opacity duration-300">
              <span className="text-gray-500 text-sm">Image unavailable</span>
            </div>
          )}
        </div>
        <CardContent className="p-4 space-y-3">
          <div className="flex justify-between items-start mb-2">
            <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
              {listing.breed}
            </span>
            {listing.price && (
              <span className="font-bold text-green-700 text-base flex-shrink-0">{listing.price}</span>
            )}
          </div>
          
          <h3 className="font-semibold text-base mb-3 line-clamp-2 leading-tight">{listing.title}</h3>
          
          <div className="flex items-center text-gray-600 mb-3">
            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm">{listing.location}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 truncate pr-2">{listing.breeder}</span>
          </div>
          
          {/* EPD/EBV Numbers */}
          {listing.evaluationData && (
            <div className="pt-2 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-6 text-sm relative">
                {/* Left Column */}
                <div className="space-y-2">
                  {listing.evaluationData.calvingEase && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">CE:</span>
                      <span className="font-medium">{listing.evaluationData.calvingEase}</span>
                    </div>
                  )}
                  {listing.evaluationData.weaningWeight && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">WW:</span>
                      <span className="font-medium">{listing.evaluationData.weaningWeight}</span>
                    </div>
                  )}
                </div>
                
                {/* Vertical Separator */}
                <Separator orientation="vertical" className="absolute left-1/2 top-0 h-full transform -translate-x-1/2" />
                
                {/* Right Column */}
                <div className="space-y-2">
                  {listing.evaluationData.birthWeight && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">BW:</span>
                      <span className="font-medium">{listing.evaluationData.birthWeight}</span>
                    </div>
                  )}
                  {listing.evaluationData.milk && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Milk:</span>
                      <span className="font-medium">{listing.evaluationData.milk}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default ListingCard;
